// src/lib/geocoding.js
import { loadGoogleMaps } from "./googleMapsLoader";

// Small in-memory cache to reduce cost + speed up UI
const _cache = new Map();

export async function reverseGeocode(coords, opts = {}) {
  const { cacheTtlMs = 5 * 60 * 1000, cachePrecision = 6 } = opts;

  // ✅ Ensure Maps JS is loaded
  await loadGoogleMaps();

  // ✅ Ensure Geocoding library is loaded (this is what provides Geocoder in importLibrary world)
  if (window.google?.maps?.importLibrary) {
    await window.google.maps.importLibrary("geocoding");
  }

  const lat = Number(coords?.lat);
  const lng = Number(coords?.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    throw new Error("reverseGeocode: invalid coords");
  }

  if (!window.google?.maps?.Geocoder) {
    throw new Error("reverseGeocode: Google Maps JS not loaded (Geocoder missing)");
  }

  const key = `${lat.toFixed(cachePrecision)},${lng.toFixed(cachePrecision)}`;
  const now = Date.now();

  const cached = _cache.get(key);
  if (cached && now - cached.at < cacheTtlMs) {
    return { formattedAddress: cached.formattedAddress, results: cached.results };
  }

  const geocoder = new window.google.maps.Geocoder();

  const res = await new Promise((resolve, reject) => {
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status !== "OK") return reject(new Error(`reverseGeocode: status=${status}`));
      resolve({ results: results || [] });
    });
  });

  const formattedAddress = res?.results?.[0]?.formatted_address || "";
  if (!formattedAddress) throw new Error("reverseGeocode: no formatted_address returned");

  _cache.set(key, { at: now, formattedAddress, results: res.results });

  return { formattedAddress, results: res.results };
}

export function clearGeocodeCache() {
  _cache.clear();
}